import React, { useState } from "react";
import PropTypes from "prop-types";

function FeedInterestCard({
  uuid,
  category,
  heading,
  description,
  type,
  possibleAnswers = [],
  onSkip,
  onSubmit,
}) {
  // Array state for multiple choice selection
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExiting, setIsExiting] = useState(false); // Controls local fade & collapse animation

  const isBooleanType = type === "BOOLEAN" || type === "BO";

  // Helper function to animate out before executing the handler
  const animateAndExecute = async (callback) => {
    if (isSubmitting || isExiting) return;
    setIsSubmitting(true);
    setIsExiting(true);

    // Wait 300ms for CSS fade & collapse transition to finish before calling parent handler
    setTimeout(async () => {
      try {
        if (callback) {
          await callback();
        }
      } finally {
        setIsSubmitting(false);
      }
    }, 300);
  };

  // Toggle selection for multiple choices
  const handleToggleAnswer = (answer) => {
    setSelectedAnswers((prev) =>
      prev.includes(answer)
        ? prev.filter((item) => item !== answer)
        : [...prev, answer],
    );
  };

  // Direct submission for YES / NO buttons in Boolean mode
  const handleBooleanResponse = (answer) => {
    animateAndExecute(
      () => onSubmit && onSubmit({ uuid, selectedAnswer: answer }),
    );
  };

  // Submission for Multiple Choice mode
  const handleSend = () => {
    if (selectedAnswers.length === 0) return;
    animateAndExecute(
      () => onSubmit && onSubmit({ uuid, selectedAnswer: selectedAnswers }),
    );
  };

  // Skip handler
  const handleSkip = () => {
    animateAndExecute(() => onSkip && onSkip(uuid));
  };

  return (
    <div
      className="mobile-outher-container"
      style={{
        transition:
          "opacity 300ms ease-out, transform 300ms ease-out, max-height 300ms cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 300ms ease-out",
        opacity: isExiting ? 0 : 1,
        transform: isExiting
          ? "scale(0.95) translateY(-8px)"
          : "scale(1) translateY(0)",
        maxHeight: isExiting ? "0px" : "600px",
        marginBottom: "0px",
        overflow: "hidden",
        pointerEvents: isExiting ? "none" : "auto",
      }}
    >
      <div className="feed-interest-card">
        {/* Header Tag */}
        <div className="interest-tag">
          <span className="sparkle-icon">
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/sparkle.svg`}
              alt="Source Icon"
              loading="lazy"
            />
          </span>
          <span>Quick Question</span>
        </div>

        {/* Heading & Description */}
        <div className="interest-body">
          {heading && <h3 className="interest-heading">{heading}</h3>}
          {description && <p className="interest-description">{description}</p>}
        </div>

        {/* Answer Options - Only rendered if NOT Boolean type */}
        {!isBooleanType && possibleAnswers.length > 0 && (
          <div className="interest-answers">
            {possibleAnswers.map((answer, index) => {
              const isSelected = selectedAnswers.includes(answer);
              return (
                <label
                  key={index}
                  className={`answer-option ${isSelected ? "selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    name={`interest-${uuid}`}
                    checked={isSelected}
                    onChange={() => handleToggleAnswer(answer)}
                  />
                  <span className="answer-text">{answer}</span>
                </label>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div className="interest-card-actions">
          {isBooleanType ? (
            /* Boolean Mode Actions: SKIP | YES | NO */
            <>
              <div></div>
              <div style={{ display: "flex", gap: "16px" }}>
                <button
                  type="button"
                  className="cta-button primary rounded small"
                  onClick={() => handleBooleanResponse("YES")}
                  disabled={isSubmitting || isExiting}
                >
                  YES
                </button>
                <button
                  type="button"
                  className="cta-button secondary rounded small"
                  onClick={() => handleBooleanResponse("NO")}
                  disabled={isSubmitting || isExiting}
                >
                  NO
                </button>
                <button
                  type="button"
                  className="cta-button primary rounded small"
                  onClick={handleSkip}
                  disabled={isSubmitting || isExiting}
                >
                  Skip
                </button>
              </div>
            </>
          ) : (
            /* Multiple Choice Mode Actions: SKIP | SEND */
            <>
              <button
                type="button"
                className="cta-button primary rounded small interest-skip"
                onClick={handleSkip}
                disabled={isSubmitting || isExiting}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/send_white.svg`}
                  alt="Source Icon"
                  loading="lazy"
                />
                <span>Skip</span>
              </button>
              <button
                type="button"
                className="cta-button primary rounded small"
                onClick={handleSend}
                disabled={
                  selectedAnswers.length === 0 || isSubmitting || isExiting
                }
              >
                SEND
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

FeedInterestCard.propTypes = {
  uuid: PropTypes.string.isRequired,
  category: PropTypes.string,
  heading: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.string.isRequired,
  possibleAnswers: PropTypes.arrayOf(PropTypes.string),
  onSkip: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default FeedInterestCard;
